import { ColDef, ICellRendererParams } from 'ag-grid-community'
import columnRenderers, { PLACEHOLDER } from 'components/column-renderers'
import { createCohortsColumn, createCohortsCsvColumn } from 'features/Cohorts'
import { formatEngagement } from 'features/Reports/helpers/formatEngagement'
import { CSVField } from 'hooks/useExportCSV'
import { EngagementOrgUserReportTypes, OrganizationalUser, UserType, UserTypeLabels } from 'types'
import { formatMetricDate, isDefined } from 'utils'
import { UserOrgAccessCodesCell } from '../../../User/components/UserOrgAccessCodesCell/UserOrgAccessCodesCell'
import { createEngagementColumn, createEngagementCsvColumn } from '../../helpers/engagementColumn'

export function useReportsEngagementOrgUser(selectedTab: EngagementOrgUserReportTypes) {
  const totalEngagementColumns: ColDef[] = [
    { headerName: 'Anonymous ID', field: 'userAnonymousId', minWidth: 174 },
    { headerName: 'First Name', field: 'userFirstName', filter: 'agTextColumnFilter' },
    { headerName: 'Last name', field: 'userLastName', filter: 'agTextColumnFilter' },
    { headerName: 'Login', field: 'userEmail', filter: 'agTextColumnFilter', minWidth: 270 },
    {
      headerName: 'Organization Name',
      field: 'organizationName',
      filter: 'agTextColumnFilter',
      minWidth: 420,
    },
    {
      headerName: 'Role',
      colId: 'userType',
      cellRenderer: (props: ICellRendererParams<{ userType: UserType[] }>) => {
        const userType = props.data?.userType
        if (isDefined(userType)) {
          const role = userType[0]
          return UserTypeLabels[role] || PLACEHOLDER
        }
        return PLACEHOLDER
      },
    },
    createCohortsColumn('userCohorts'),
    {
      headerName: 'Assess Codes',
      field: 'ownedAccessCodes',
      minWidth: 250,
      cellRenderer: (props: ICellRendererParams<OrganizationalUser>) => {
        const ownedAccessCodes = props.data?.ownedAccessCodes
        if (ownedAccessCodes?.length) {
          return <UserOrgAccessCodesCell accessCodes={ownedAccessCodes} />
        }
        return PLACEHOLDER
      },
    },
    {
      headerName: 'First Login',
      field: 'userFirstLoggedInAt',
      cellRenderer: columnRenderers.date('userFirstLoggedInAt', 'YYYY-MM-DD HH:mm:ss'),
    },
    {
      headerName: 'Last Login',
      field: 'userLastLoggedInAt',
      cellRenderer: columnRenderers.date('userLastLoggedInAt', 'YYYY-MM-DD HH:mm:ss'),
    },
    {
      headerName: 'Number Of Logins',
      field: 'userTotalNumbersOfLogin',
      minWidth: 230,
    },
    createEngagementColumn('Total Time Online', 'totalEngagement'),
  ]

  const totalEngagementCsvFields: CSVField[] = [
    { label: 'Anonymous ID', value: 'userAnonymousId' },
    { label: 'First Name', value: 'userFirstName' },
    { label: 'Last name', value: 'userLastName' },
    { label: 'Login', value: 'userEmail' },
    { label: 'Organization Name', value: 'organizationName' },
    {
      label: 'Role',
      value: ({ userType }: { userType: UserType[] }) => {
        if (isDefined(userType)) {
          const role = userType[0]
          return UserTypeLabels[role]
        }
        return PLACEHOLDER
      },
    },
    createCohortsCsvColumn('userCohorts'),
    {
      label: 'Institutional Assess Codes',
      value: ({ ownedAccessCodes }: OrganizationalUser) => {
        if (ownedAccessCodes?.length) {
          return ownedAccessCodes.map(({ code }) => code).join(', ')
        }
        return PLACEHOLDER
      },
    },
    {
      label: 'First Login',
      value: ({ userFirstLoggedInAt }) => formatMetricDate(userFirstLoggedInAt),
    },
    {
      label: 'Last Login',
      value: ({ userLastLoggedInAt }) => formatMetricDate(userLastLoggedInAt),
    },
    { label: 'Numbers of Logins', value: 'userTotalNumbersOfLogin' },
    createEngagementCsvColumn('Total Time Online', 'totalEngagement'),
  ]

  const loginColumns: ColDef[] = [
    { headerName: 'Anonymous ID', field: 'userAnonymousId', minWidth: 174 },
    { headerName: 'First Name', field: 'userFirstName', filter: 'agTextColumnFilter' },
    { headerName: 'Last name', field: 'userLastName', filter: 'agTextColumnFilter' },
    { headerName: 'Login', field: 'userEmail', filter: 'agTextColumnFilter', minWidth: 270 },
    {
      headerName: 'Organization Name',
      field: 'organizationName',
      filter: 'agTextColumnFilter',
      minWidth: 420,
    },
    {
      headerName: 'Role',
      colId: 'userType',
      cellRenderer: (props: ICellRendererParams<{ userType: UserType[] }>) => {
        const userType = props.data?.userType
        if (isDefined(userType)) {
          const role = userType[0]
          return UserTypeLabels[role] || PLACEHOLDER
        }
        return PLACEHOLDER
      },
    },
    createCohortsColumn('userCohorts'),
    {
      headerName: 'Assess Codes',
      field: 'ownedAccessCodes',
      minWidth: 250,
      cellRenderer: (props: ICellRendererParams<OrganizationalUser>) => {
        const ownedAccessCodes = props.data?.ownedAccessCodes
        if (ownedAccessCodes?.length) {
          return <UserOrgAccessCodesCell accessCodes={ownedAccessCodes} />
        }
        return PLACEHOLDER
      },
    },
    {
      headerName: 'Login Date',
      field: 'startedAt',
      cellRenderer: columnRenderers.date('startedAt', 'YYYY-MM-DD HH:mm:ss'),
    },
    createEngagementColumn('Total Login Time', 'totalTime'),
  ]

  const loginCsvFields: CSVField[] = [
    { label: 'Anonymous ID', value: 'userAnonymousId' },
    { label: 'First Name', value: 'userFirstName' },
    { label: 'Last name', value: 'userLastName' },
    { label: 'Organization Name', value: 'organizationName' },
    {
      label: 'Role',
      value: ({ userType }: { userType: UserType[] }) => {
        if (isDefined(userType)) {
          const role = userType[0]
          return UserTypeLabels[role]
        }
        return PLACEHOLDER
      },
    },
    createCohortsCsvColumn('userCohorts'),
    {
      label: 'Institutional Assess Codes',
      value: ({ ownedAccessCodes }: OrganizationalUser) => {
        if (ownedAccessCodes?.length) {
          return ownedAccessCodes.map(({ code }) => code).join(', ')
        }
        return PLACEHOLDER
      },
    },
    { label: 'Login Date', value: ({ startedAt }) => formatMetricDate(startedAt) },
    {
      label: 'Total Login Time',
      value: ({ totalTime }) => formatEngagement(totalTime),
    },
  ]

  const tabOptions = {
    [EngagementOrgUserReportTypes.TotalEngagement]: {
      title: 'Total Engagement per Org user',
      columns: totalEngagementColumns,
      csvFields: totalEngagementCsvFields,
      csvName: 'Total Engagement per Org user.csv',
    },
    [EngagementOrgUserReportTypes.Login]: {
      title: 'Login per Org user',
      columns: loginColumns,
      csvFields: loginCsvFields,
      csvName: 'Login per Org user.csv',
    },
  }

  const activeTabColumns = tabOptions[selectedTab].columns

  return {
    tabsOrder: [EngagementOrgUserReportTypes.TotalEngagement, EngagementOrgUserReportTypes.Login],
    tabOptions,
    activeTabColumns,
  }
}
